/**
 * Honest, client-readable copy for every tool Emjae can call.
 *
 * The trace shows a client what the agent actually did, and `agileco-list-posts`
 * tells a client nothing. The raw name still goes in each row's `title`, so
 * support can read what the agent really called without the client having to.
 *
 * The three read-only facades that back these names:
 *   WordPress  backend/api/routes/emjae_mcp_proxy.py      `WORDPRESS_READ_TOOLS`
 *   Core       backend/api/routes/emjae_core_mcp_proxy.py `CORE_TOOLS`
 *   Analytics  backend/api/routes/emjae_aa_mcp_proxy.py   `AA_READ_TOOLS`
 * plus Eve's own tools (built in, and backend/emjae-eve/agent/tools/) and the
 * handful of in-process steps chat_hub.py reports as tools.
 *
 * WHAT ARRIVES IS NOT THE REGISTRATION NAME. Eve qualifies every tool it
 * reaches through an MCP connection as `<connection>__<tool>`, passing the
 * remote name through verbatim — verified against the
 * published eve 0.47.3 tarball, `dist/src/execution/tools/connection-search.js`:
 *
 *     qualifiedConnectionToolName(e, t) { return `${e}__${t}` }
 *
 * The connection name is the filename in backend/emjae-eve/agent/connections/,
 * so `agileco-list-posts` reaches this file as `wordpress__agileco-list-posts`.
 * `canonical` below reduces both spellings to the same key, and the namespace
 * fallback keys on the connection prefix for exactly this reason.
 *
 * A name missing from the table is not a bug to fix before shipping — the
 * fallback below still says something true — but adding the entry is better
 * than leaving the fallback to guess.
 */

/**
 * Eve reports whatever its connection layer calls a tool, and that spelling has
 * changed shape before (`core/site-health`, `mcp__core__site-health`). Reduce a
 * name to the part that identifies the tool rather than the transport: drop any
 * connection and facade prefix, settle on one word separator, lowercase.
 */
function canonical(name: string): string {
  const withoutTransport = name.split('__').pop() ?? name;
  const leaf = withoutTransport.split('/').pop() ?? withoutTransport;
  return leaf
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/^(?:agileco|core)_/, '');
}

const TOOL_LABELS: Record<string, string> = {
  // ── WordPress, the client's own site ──────────────────────────────────
  list_posts: 'Reading your blog posts',
  get_post: 'Opening one of your posts',
  list_pages: 'Reading your site pages',
  list_media: 'Looking through your media library',
  get_categories: 'Reading your categories',
  get_tags: 'Reading your tags',
  get_site_info: 'Checking your WordPress setup',
  seo_list_content: 'Reading your content plan',
  seo_get_content: 'Opening one planned article',
  seo_list_topical_maps: 'Reading your topical maps',
  seo_list_templates: 'Reading your content templates',
  seo_get_cluster: 'Opening a keyword cluster',
  seo_get_schema_overview: 'Checking your schema markup',
  replace_paragraph: 'Preparing a paragraph change for your approval',
  read_page_source: 'Reading how one of your pages is built',
  replace_text: 'Changing text on your page',
  update_title: 'Changing a page title',

  // ── Core, the dashboard the client logs into ──────────────────────────
  site_health: 'Checking your site health score',
  audit_issues: 'Reading your audit issues',
  content_status: 'Checking where your content stands',
  content_calendar: 'Reading your content calendar',
  search_performance: 'Reading your Search Console traffic',
  top_pages: 'Finding your best-performing pages',
  keyword_plan: 'Reading your keyword plan',
  list_documents: 'Listing the documents you uploaded',
  read_document: 'Reading one of your documents',
  site_inventory: 'Reading the pages we have indexed',

  // ── AgencyAnalytics, the client's numbers ─────────────────────────────
  browse_client_reports: 'Browsing your reports',
  read_client_report: 'Reading one of your reports',
  browse_client_dashboards: 'Browsing your dashboards',
  read_client_dashboard: 'Reading one of your dashboards',
  browse_client_data_sources: 'Checking which data sources are connected',
  // No `read_client_data_source`: it sits in AA_RAW_QUERY_TOOLS but not in
  // AA_READ_TOOLS, and emjae_aa_mcp_proxy.py gates tools/call on the latter.
  browse_client_custom_metrics: 'Browsing your custom metrics',
  read_client_custom_metrics: 'Reading your custom metrics',
  read_client_metrics: 'Reading your headline metrics',
  read_client_ads: 'Reading your ad performance',
  read_client_ad_manager: 'Reading your ad manager numbers',
  read_client_traffic: 'Reading your website traffic',
  read_client_audience: 'Reading your audience breakdown',
  read_client_pages: 'Reading how your pages perform',
  read_client_content: 'Reading how your content performs',
  read_client_keywords: 'Reading your keyword rankings',
  read_client_conversions: 'Reading your conversions',
  read_client_calls: 'Reading your tracked calls',
  read_client_forms: 'Reading your form submissions',
  read_client_crm: 'Reading your CRM records',
  read_client_ecommerce: 'Reading your store numbers',
  read_client_email: 'Reading your email campaign numbers',
  read_client_reviews: 'Reading your customer reviews',
  read_client_reputation: 'Reading your reputation scores',
  read_client_insights: 'Reading your analytics insights',
  read_client_knowledge: 'Reading your knowledge base',
  read_client_site_audit: 'Reading your site audit',
  read_client_ai_tracker: 'Reading your AI search visibility',

  // ── Eve's own tools ───────────────────────────────────────────────────
  // `connection_search` is the one to get right: Eve makes the model find a
  // connection before it can call anything on it, so this is the most frequent
  // name in the whole stream.
  connection_search: 'Looking for the right data source',
  web_read: 'Opening a page on the web',
  offer_actions: 'Adding a shortcut to the answer',
  todo: 'Planning the steps',
  task_update: 'Revising my plan',
  task_cancel: 'Dropping a step from my plan',
  ask_question: 'Asking you something',
  load_skill: 'Checking how to do this',
  sleep: "I'm waiting",

  // ── What emjae_agent_client.py falls back to when Eve names nothing ────
  // `_action_name` yields the bare kind, or the literal "tool"/"action".
  // Without these a row renders one of those words raw at the client.
  tool: "I'm running a tool",
  action: "I'm working",
  subagent_call: 'Handing off to a helper',
  remote_agent_call: 'Handing off to a helper',

  // ── Steps chat_hub.py runs in process and reports as tools ────────────
  'proposal.generate': 'Building your proposal',
  'google_ads.campaigns_live': 'Reading your live Google Ads campaigns',
  'google_ads.weekly_snapshot': "Reading this week's Google Ads numbers",
  'google_ads.weekly_trigger': 'Starting the weekly Google Ads review',
  'internal_pages.inventory': 'Reading the pages we have indexed',
  'editor_ai.ask': 'Working on the article in the editor',
  'service_page.generate': 'Building a service page',

  // ── Page tools, run by the BROWSER on the screen the user is looking at ──
  // Named exactly, so the row says which one ran. The `planner_` family
  // fallback below still catches any that land before this table does.
  planner_find_articles: 'Finding articles in your planner',
  planner_find_keywords: 'Finding keywords in your feed',
};

/**
 * What a whole family of tools does, for a name the table has not caught up
 * with. Vaguer than the real entry and still true, which is the trade a
 * fallback exists to make.
 */
function namespaceLabel(rawName: string, key: string): string | null {
  if (rawName.startsWith('google_ads.')) return 'Working with your Google Ads account';
  // Page tools Eve hands to the BROWSER to execute (the WebMCP seam). Exact
  // registered names land with that lane; the family keeps any of them honest
  // in the meantime. Checked on the key too, in case a transport ever
  // qualifies them the way MCP connection names are qualified.
  if (rawName.startsWith('planner_') || key.startsWith('planner_')) {
    return 'Working in your content planner';
  }
  if (rawName.startsWith('bb_') || key.startsWith('bb_')) {
    return 'Working in your Brand Bible';
  }
  // Verb first, since browsing and reading are different acts; then the
  // connection, which is what a qualified name actually carries.
  if (key.startsWith('browse_client_')) return 'Browsing your analytics';
  if (key.startsWith('read_client_')) return 'Reading your analytics';
  if (
    rawName.startsWith('wordpress__') ||
    rawName.startsWith('agileco-') ||
    rawName.startsWith('agileco/')
  ) {
    return 'Working with your WordPress site';
  }
  if (rawName.startsWith('core__') || rawName.startsWith('core_') || rawName.startsWith('core/')) {
    return 'Reading your Core dashboard';
  }
  if (rawName.startsWith('agencyanalytics__')) return 'Reading your analytics';
  return null;
}

/** `seo_get_cluster` → `Seo get cluster`. The last resort, and still readable. */
function humanize(key: string): string {
  const words = key.replace(/[._]+/g, ' ').trim();
  if (!words) return "I'm working";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The line a client reads on a trace row. Never empty. */
export function toolLabel(rawName: string): string {
  const name = rawName.trim();
  if (!name) return "I'm working";
  const key = canonical(name);
  return TOOL_LABELS[key] ?? TOOL_LABELS[name] ?? namespaceLabel(name, key) ?? humanize(key);
}
